import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, HelpCircle, DollarSign, Percent, Hash, Check, AlertTriangle, TrendingUp, Copy, Sparkles, FileDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AIAnalysis } from './AIAnalysis';
import { DIYSimulator } from './DIYSimulator';
import { useCalculator, formatCurrency, formatNumber } from '../context/CalculatorContext';
import { useAuth } from '../context/AuthContext';
import { LeadGate } from './LeadGate';
import { AdSlot } from './AdSlot';
import { exportPresupuestoPDF } from '../utils/exportPresupuestoPDF';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

// Industry CPC data for Meta/TikTok
const INDUSTRY_CPC = [
  { id: 'alimentos', name: 'Alimentos y bebidas / Materias primas', cpc: 80 },
  { id: 'hogar', name: 'Hogar, decoración y mascotas', cpc: 66 },
  { id: 'automotriz', name: 'Automotriz', cpc: 85 },
  { id: 'herramientas', name: 'Herramientas e insumos', cpc: 85 },
  { id: 'tecnologia', name: 'Tecnología', cpc: 90 },
  { id: 'servicios', name: 'Servicios contables y legales', cpc: 78 },
  { id: 'moda', name: 'Moda', cpc: 89 },
  { id: 'industrial', name: 'Industrial / Construcción', cpc: 90 },
  { id: 'inmobiliaria', name: 'Inmobiliaria', cpc: 140 },
  { id: 'medico', name: 'Sector médico', cpc: 60 },
  { id: 'otro', name: 'Otra industria', cpc: 80 },
];

// Feature chips
const FEATURE_CHIPS = [
  'Presupuesto mensual',
  'Leads y clics necesarios',
  'CPC y CPL máximos',
  'ROAS esperado',
  'Intensidad del marketing'
];

// Field with tooltip component
function FieldWithTooltip({ label, tooltip, badge, badgeColor, required, children, note, fullWidth = false }) {
  const badgeColors = {
    money: 'bg-blue-100 text-blue-700',
    percent: 'bg-emerald-100 text-emerald-700',
    number: 'bg-slate-100 text-slate-600',
  };
  
  const badgeIcons = {
    money: <DollarSign className="h-3 w-3" />,
    percent: <Percent className="h-3 w-3" />,
    number: <Hash className="h-3 w-3" />,
  };

  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <div className="flex items-center gap-2 mb-2">
        {badge && (
          <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${badgeColors[badge]}`}>
            {badgeIcons[badge]}
          </span>
        )}
        <label className="text-sm font-semibold text-slate-700">{label}</label>
        {required && <span className="text-red-500 text-sm">*</span>}
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-slate-400 hover:text-slate-600 transition-colors">
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent 
                side="top" 
                className="max-w-xs text-sm bg-slate-900 text-white p-3 rounded-lg shadow-xl z-50"
              >
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {children}
      {note && <p className="text-xs text-slate-500 mt-1.5">{note}</p>}
    </div>
  );
}

export function MetaTrafficCalculator() {
  const { addToHistory } = useCalculator();
  const { isAdmin } = useAuth();
  
  // Platform state
  const [platform, setPlatform] = useState('google'); // google, meta, tiktok
  
  // Google Ads specific
  const [cpcGoogle, setCpcGoogle] = useState('');
  
  // Meta/TikTok specific
  const [industry, setIndustry] = useState('');
  
  // Business variables
  const [ticketPromedio, setTicketPromedio] = useState('');
  const [objetivoVentas, setObjetivoVentas] = useState('');
  const [conversionVisitanteLead, setConversionVisitanteLead] = useState('');
  const [tasaCierre, setTasaCierre] = useState('');
  const [cpaObjetivo, setCpaObjetivo] = useState('');
  
  const [copied, setCopied] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Get CPC reference based on platform
  const cpcReferencia = useMemo(() => {
    if (platform === 'google') {
      return cpcGoogle ? parseFloat(cpcGoogle) : null;
    }
    if (industry) {
      const ind = INDUSTRY_CPC.find(i => i.id === industry);
      return ind ? ind.cpc : null;
    }
    return null;
  }, [platform, cpcGoogle, industry]);

  // Check if all required fields are filled
  const allRequiredFilled = useMemo(() => {
    const hasTicket = ticketPromedio && parseFloat(ticketPromedio) > 0;
    const hasObjetivo = objetivoVentas && parseInt(objetivoVentas) > 0;
    const hasConversion = conversionVisitanteLead && parseFloat(conversionVisitanteLead) > 0;
    const hasCierre = tasaCierre && parseFloat(tasaCierre) > 0;
    
    return hasTicket && hasObjetivo && hasConversion && hasCierre;
  }, [ticketPromedio, objetivoVentas, conversionVisitanteLead, tasaCierre]);

  // CALCULATIONS
  const calculations = useMemo(() => {
    if (!allRequiredFilled) return null;

    const ticket = parseFloat(ticketPromedio);
    const ventas = parseInt(objetivoVentas);
    const conversionLead = parseFloat(conversionVisitanteLead) / 100;
    const cierre = parseFloat(tasaCierre) / 100;
    const cpaInput = cpaObjetivo ? parseFloat(cpaObjetivo) : null;

    // CPA efectivo = CPA ingresado ó ticket × 10% si está vacío
    const cpaEfectivo = cpaInput || ticket * 0.10;
    const cpAFueEstimado = !cpaInput;

    // Presupuesto mensual = CPA efectivo × objetivo de ventas
    const presupuestoMensual = cpaEfectivo * ventas;

    // Leads necesarios = ventas objetivo ÷ (% cierre ÷ 100)
    const leadsNecesarios = cierre > 0 ? Math.ceil(ventas / cierre) : 0;

    // Clics necesarios = leads necesarios ÷ (% conversión ÷ 100)
    const clicsNecesarios = conversionLead > 0 ? Math.ceil(leadsNecesarios / conversionLead) : 0;

    // CPL máximo = presupuesto ÷ leads necesarios
    const cplMaximo = leadsNecesarios > 0 ? presupuestoMensual / leadsNecesarios : 0;

    // CPC máximo = presupuesto ÷ clics necesarios
    const cpcMaximo = clicsNecesarios > 0 ? presupuestoMensual / clicsNecesarios : 0;

    // ROAS esperado = ticket ÷ CPA efectivo
    const roasEsperado = cpaEfectivo > 0 ? ticket / cpaEfectivo : 0;

    // Ingresos proyectados = ticket × ventas objetivo
    const ingresosProyectados = ticket * ventas;

    // Intensidad Mkt % = (presupuesto ÷ ingresos proyectados) × 100
    const intensidadMkt = ingresosProyectados > 0 ? (presupuestoMensual / ingresosProyectados) * 100 : 0;

    return {
      cpaEfectivo,
      cpAFueEstimado,
      presupuestoMensual,
      leadsNecesarios,
      clicsNecesarios,
      cplMaximo,
      cpcMaximo,
      roasEsperado,
      ingresosProyectados,
      intensidadMkt,
      ticket,
      ventas,
    };
  }, [allRequiredFilled, ticketPromedio, objetivoVentas, conversionVisitanteLead, tasaCierre, cpaObjetivo]);

  // Viability check
  const viabilidad = useMemo(() => {
    if (!calculations || !cpcReferencia) return null;
    
    const { cpcMaximo } = calculations;
    const esViable = cpcMaximo >= cpcReferencia;
    const diferencia = Math.abs(cpcMaximo - cpcReferencia);
    const fuenteCpc = platform === 'google' ? 'de Google Ads' : `de la industria`;

    return {
      esViable,
      cpcMaximo,
      cpcReferencia,
      diferencia,
      fuenteCpc,
    };
  }, [calculations, cpcReferencia, platform]);

  // ROAS category
  const getRoasCategory = (roas) => {
    if (roas >= 5) return { label: 'Optimista', color: 'bg-emerald-100 text-emerald-700' };
    if (roas >= 2) return { label: 'Realista', color: 'bg-blue-100 text-blue-700' };
    return { label: 'Conservador', color: 'bg-amber-100 text-amber-700' };
  };

  // Intensity category
  const getIntensityCategory = (intensity) => {
    if (intensity < 10) return { label: 'Intensidad baja — alta eficiencia', color: 'bg-emerald-100 text-emerald-700' };
    if (intensity <= 20) return { label: 'Intensidad moderada', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Intensidad alta — revisa tu estrategia', color: 'bg-red-100 text-red-700' };
  };

  // AI context
  const aiContext = useMemo(() => {
    if (!calculations) return '';
    return `Análisis de presupuesto publicitario:
- Plataforma: ${platform === 'google' ? 'Google Ads' : platform === 'meta' ? 'Meta Ads' : 'TikTok Ads'}
- Ticket promedio: $${formatNumber(calculations.ticket)} MXN
- Objetivo: ${calculations.ventas} ventas/mes
- Presupuesto mensual calculado: $${formatNumber(calculations.presupuestoMensual)} MXN
- Leads necesarios: ${calculations.leadsNecesarios}
- Clics necesarios: ${calculations.clicsNecesarios}
- CPC máximo: $${calculations.cpcMaximo.toFixed(2)} MXN
- CPL máximo: $${calculations.cplMaximo.toFixed(2)} MXN
- ROAS esperado: ${calculations.roasEsperado.toFixed(1)}x
- Intensidad del marketing: ${calculations.intensidadMkt.toFixed(1)}%
- CPA ${calculations.cpAFueEstimado ? '(estimado al 10% del ticket)' : 'objetivo'}: $${formatNumber(calculations.cpaEfectivo)} MXN
${viabilidad ? `- Viabilidad: ${viabilidad.esViable ? 'VIABLE' : 'INSUFICIENTE'} (CPC máx ${formatCurrency(viabilidad.cpcMaximo)} vs CPC ref ${formatCurrency(viabilidad.cpcReferencia)})` : ''}

Analiza este escenario y proporciona recomendaciones específicas para optimizar el presupuesto y alcanzar los objetivos de ventas.`;
  }, [calculations, platform, viabilidad]);

  // Copy summary
  const handleCopySummary = async () => {
    if (!calculations) return;
    
    const roasCat = getRoasCategory(calculations.roasEsperado);
    const intCat = getIntensityCategory(calculations.intensidadMkt);
    
    const summary = `📊 PRESUPUESTO PUBLICITARIO MENSUAL
Plataforma: ${platform === 'google' ? 'Google Ads' : platform === 'meta' ? 'Meta Ads' : 'TikTok Ads'}

═══════════════════════════════════════
RESULTADOS ESTIMADOS
═══════════════════════════════════════
Presupuesto mensual: ${formatCurrency(calculations.presupuestoMensual)}
Leads necesarios: ${formatNumber(calculations.leadsNecesarios)}
Clics necesarios: ${formatNumber(calculations.clicsNecesarios)}
CPL máximo: ${formatCurrency(calculations.cplMaximo)}
CPC máximo: ${formatCurrency(calculations.cpcMaximo)}
ROAS esperado: ${calculations.roasEsperado.toFixed(1)}x (${roasCat.label})

═══════════════════════════════════════
PROYECCIÓN
═══════════════════════════════════════
Ingresos proyectados: ${formatCurrency(calculations.ingresosProyectados)}
Intensidad del marketing: ${calculations.intensidadMkt.toFixed(1)}% (${intCat.label})
CPA ${calculations.cpAFueEstimado ? '(estimado)' : 'objetivo'}: ${formatCurrency(calculations.cpaEfectivo)}

${viabilidad ? `═══════════════════════════════════════
VIABILIDAD
═══════════════════════════════════════
${viabilidad.esViable ? '✓ Presupuesto viable' : '⚠ Presupuesto insuficiente'}
CPC máximo: ${formatCurrency(viabilidad.cpcMaximo)} vs CPC ${viabilidad.fuenteCpc}: ${formatCurrency(viabilidad.cpcReferencia)}` : ''}

*Calculado con la Calculadora de Pauta de Vitamina Online*`;

    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = summary;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Presupuesto Publicitario Mensual</h1>
              <p className="text-slate-500">Calculadora completa de inversión digital</p>
            </div>
          </div>
        </motion.div>

        {/* BLOQUE 1 - Introducción */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 bg-slate-50 border-l-4 border-blue-500 rounded-r-xl"
        >
          <p className="text-slate-700 leading-relaxed">
            Ingresa los datos de tu negocio y obtén en segundos el presupuesto mensual recomendado para publicidad digital, cuántos clics y leads necesitas generar, cuánto deberías pagar como máximo por cada clic, y qué tan eficiente será tu inversión frente a tus ingresos proyectados.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {FEATURE_CHIPS.map((chip) => (
              <span key={chip} className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                {chip}
              </span>
            ))}
          </div>
        </motion.div>

        {/* BLOQUE 2 - Selector de plataforma */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
        >
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Plataforma</h2>
          
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { id: 'google', label: 'Google Ads' },
              { id: 'meta', label: 'Meta Ads' },
              { id: 'tiktok', label: 'TikTok Ads' },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  setPlatform(p.id);
                  if (p.id === 'google') setIndustry('');
                  else setCpcGoogle('');
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  platform === p.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`platform-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {platform === 'google' ? (
              <motion.div
                key="google-cpc"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <FieldWithTooltip
                  label="CPC — Costo por Clic (MXN)"
                  badge="money"
                  required
                  tooltip="Cuánto pagas en promedio cada vez que alguien hace clic en tu anuncio. Obtén este valor del Planificador de Palabras Clave de Google Ads buscando tus términos principales."
                  note="Consulta el Planificador de Palabras Clave de Google Ads para obtener este dato."
                >
                  <div className="relative max-w-xs">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={cpcGoogle}
                      onChange={(e) => setCpcGoogle(e.target.value)}
                      placeholder="ej. 12.50"
                      className="h-12 pl-8 font-mono"
                      data-testid="cpc-google-input"
                    />
                  </div>
                </FieldWithTooltip>
              </motion.div>
            ) : (
              <motion.div
                key="industry-select"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <FieldWithTooltip
                  label="Industria del cliente"
                  required
                  tooltip="El CPC de referencia varía por industria. Se usa como estimado del costo por clic en Meta o TikTok para calcular la viabilidad de tu presupuesto."
                >
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger className="h-12 max-w-md" data-testid="industry-select">
                      <SelectValue placeholder="Selecciona la industria" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_CPC.map((ind) => (
                        <SelectItem key={ind.id} value={ind.id}>
                          {ind.name} — ${ind.cpc} CPC
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldWithTooltip>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* BLOQUE 3 - Variables del negocio */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
        >
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Variables del Negocio</h2>
          <p className="text-xs text-slate-400 mb-6">* Campos obligatorios para calcular</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldWithTooltip
              label="Valor de una venta cerrada (MXN)"
              badge="money"
              required
              tooltip="Ticket promedio. Cuánto dinero recibes cuando cierras una venta. Si ofreces un servicio mensual de $3,000, tu ticket es $3,000. Si tus precios varían, usa el promedio."
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                <Input
                  type="number"
                  value={ticketPromedio}
                  onChange={(e) => setTicketPromedio(e.target.value)}
                  placeholder="ej. 5,000"
                  className="h-12 pl-8 font-mono"
                  data-testid="ticket-input"
                />
              </div>
            </FieldWithTooltip>

            <FieldWithTooltip
              label="Objetivo de ventas mensuales"
              badge="number"
              required
              tooltip="Cuántos clientes nuevos quieres cerrar cada mes gracias a publicidad digital. Ejemplo: si quieres 10 ventas nuevas al mes, escribe 10."
            >
              <Input
                type="number"
                value={objetivoVentas}
                onChange={(e) => setObjetivoVentas(e.target.value)}
                placeholder="ej. 10 clientes/mes"
                className="h-12"
                data-testid="objetivo-ventas-input"
              />
            </FieldWithTooltip>

            <FieldWithTooltip
              label="% Conversión visitante → lead"
              badge="percent"
              required
              tooltip="Tasa de conversión web. De cada 100 personas que hacen clic en tu anuncio, ¿cuántas te dejan sus datos o te contactan? Si de 100 visitas obtienes 10 leads, tu tasa es 10%."
            >
              <div className="relative">
                <Input
                  type="number"
                  value={conversionVisitanteLead}
                  onChange={(e) => setConversionVisitanteLead(e.target.value)}
                  placeholder="ej. 30"
                  className="h-12 pr-8"
                  data-testid="conversion-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
              </div>
            </FieldWithTooltip>

            <FieldWithTooltip
              label="% Cierre de ventas (lead → cliente)"
              badge="percent"
              required
              tooltip="Tasa de cierre. De cada 100 leads que te contactan, ¿cuántos terminan comprando? Ejemplo: si de 10 prospectos cierras 2 ventas, tu tasa de cierre es 20%."
            >
              <div className="relative">
                <Input
                  type="number"
                  value={tasaCierre}
                  onChange={(e) => setTasaCierre(e.target.value)}
                  placeholder="ej. 20"
                  className="h-12 pr-8"
                  data-testid="cierre-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">%</span>
              </div>
            </FieldWithTooltip>

            <FieldWithTooltip
              label="CPA — Costo por Adquisición objetivo (MXN)"
              badge="money"
              fullWidth
              tooltip="CPA — Costo por Adquisición. Lo máximo que estás dispuesto a pagar para conseguir un cliente nuevo. Si lo dejas vacío, la calculadora usará el 10% de tu ticket como punto de partida. ¿Por qué 10%? Es el umbral de rentabilidad básico en la mayoría de los negocios de servicios: invertir más del 10% de tu venta en conseguirla suele comprometer el margen, mientras que menos del 10% indica campañas muy eficientes. Es un estimado conservador, no un límite absoluto."
              note="Opcional. Si no lo tienes definido, se usará el 10% de tu ticket como estimado base de rentabilidad."
            >
              <div className="relative max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                <Input
                  type="number"
                  value={cpaObjetivo}
                  onChange={(e) => setCpaObjetivo(e.target.value)}
                  placeholder="Opcional — se calcula al 10% del ticket"
                  className="h-12 pl-8 font-mono"
                  data-testid="cpa-input"
                />
              </div>
            </FieldWithTooltip>
          </div>
        </motion.div>

        {/* AdSense In-article Ad - between inputs and results */}
        {!isAdmin && (
          <AdSlot slotId="4444444444" variant="inarticle" testId="meta-traffic-ad-inarticle" className="mb-8" />
        )}

        <LeadGate
          active={allRequiredFilled}
          title="Desbloquea tus Resultados Estimados"
          description="Registra tu correo corporativo para ver el presupuesto mensual, leads/clics necesarios, ROAS esperado, viabilidad y simulador DIY."
        >
        {/* BLOQUE 4 - Resultados estimados */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
        >
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-6">Resultados Estimados</h2>

          {!allRequiredFilled ? (
            <div className="py-12 border-2 border-dashed border-slate-200 rounded-xl text-center">
              <p className="text-slate-400">Completa los campos con * para ver tus resultados.</p>
            </div>
          ) : calculations && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Presupuesto mensual - destacado */}
              <div className="col-span-2 md:col-span-1 p-6 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                <p className="text-sm text-blue-600 font-medium mb-1">Presupuesto mensual</p>
                <p className="text-3xl md:text-4xl font-bold text-blue-700 font-mono">
                  {formatCurrency(calculations.presupuestoMensual)}
                </p>
                <p className="text-xs text-blue-500 mt-2">Inversión total recomendada en publicidad digital</p>
              </div>

              {/* Leads necesarios */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-600 font-medium mb-1">Leads necesarios</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {formatNumber(calculations.leadsNecesarios)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Prospectos a generar para alcanzar tu meta</p>
              </div>

              {/* Clics necesarios */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-600 font-medium mb-1">Clics necesarios</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {formatNumber(calculations.clicsNecesarios)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Visitas requeridas según tu tasa de conversión</p>
              </div>

              {/* CPL máximo */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-600 font-medium mb-1">CPL máximo</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {formatCurrency(calculations.cplMaximo)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Costo Por Lead: máximo a pagar por cada prospecto</p>
              </div>

              {/* CPC máximo */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-600 font-medium mb-1">CPC máximo a pagar</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {formatCurrency(calculations.cpcMaximo)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Costo Por Clic: techo por cada visita a tu sitio</p>
              </div>

              {/* ROAS esperado */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-600 font-medium mb-1">ROAS esperado</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {calculations.roasEsperado.toFixed(1)}x
                </p>
                <p className="text-xs text-slate-400 mt-1">Retorno Sobre Inversión Publicitaria: $ ganados por cada $1 invertido</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* BLOQUE 5 - Indicador de viabilidad */}
        <AnimatePresence>
          {viabilidad && calculations && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`mb-8 p-6 rounded-2xl border-2 ${
                viabilidad.esViable 
                  ? 'bg-emerald-50 border-emerald-300' 
                  : 'bg-red-50 border-red-300'
              }`}
            >
              <div className="flex items-start gap-4">
                {viabilidad.esViable ? (
                  <Check className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                )}
                <div>
                  <h3 className={`font-bold text-lg ${viabilidad.esViable ? 'text-emerald-800' : 'text-red-800'}`}>
                    {viabilidad.esViable ? 'Presupuesto viable' : 'Presupuesto insuficiente'}
                  </h3>
                  <p className={`mt-1 ${viabilidad.esViable ? 'text-emerald-700' : 'text-red-700'}`}>
                    {viabilidad.esViable 
                      ? `Tu CPC máximo (${formatCurrency(viabilidad.cpcMaximo)}) cubre el CPC ${viabilidad.fuenteCpc} (${formatCurrency(viabilidad.cpcReferencia)}). Tienes margen real para alcanzar tu objetivo de ${calculations.ventas} ventas con ${formatCurrency(calculations.presupuestoMensual)}/mes.`
                      : `Tu CPC máximo (${formatCurrency(viabilidad.cpcMaximo)}) está ${formatCurrency(viabilidad.diferencia)} por debajo del CPC ${viabilidad.fuenteCpc} (${formatCurrency(viabilidad.cpcReferencia)}). Opciones: aumenta tu presupuesto, reduce tu meta de ventas, o mejora tu tasa de cierre.`
                    }
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* BLOQUE 6 - Resumen en lenguaje natural */}
        {calculations && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8 bg-slate-50 rounded-2xl border border-slate-200 p-6 space-y-6"
          >
            {/* Párrafo 1 - Flujo de inversión */}
            <div>
              <p className="text-slate-700 leading-relaxed">
                Con un presupuesto de <strong>{formatCurrency(calculations.presupuestoMensual)}</strong> mensuales, generarás aproximadamente <strong>{formatNumber(calculations.leadsNecesarios)}</strong> leads. Para conseguirlos necesitas <strong>{formatNumber(calculations.clicsNecesarios)}</strong> clics. El CPL máximo es <strong>{formatCurrency(calculations.cplMaximo)}</strong> — el límite de lo que puedes pagar por cada lead sin comprometer tu CPA. El CPC máximo es <strong>{formatCurrency(calculations.cpcMaximo)}</strong> — el techo de costo por clic para que los leads resultantes sigan siendo rentables dentro de tu presupuesto. Tu CPA objetivo es <strong>{formatCurrency(calculations.cpaEfectivo)}</strong>{calculations.cpAFueEstimado && <span className="text-slate-500"> (estimado al 10% de tu ticket como base de rentabilidad)</span>}.
              </p>
            </div>

            {/* Párrafo 2 - ROAS */}
            <div>
              {(() => {
                const roasCat = getRoasCategory(calculations.roasEsperado);
                return (
                  <p className="text-slate-700 leading-relaxed">
                    El ROAS esperado es <strong>{calculations.roasEsperado.toFixed(1)}x</strong> <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roasCat.color}`}>{roasCat.label}</span> — por cada $1 invertido en publicidad generarás ${calculations.roasEsperado.toFixed(2)} en ventas. Con {calculations.ventas} clientes al mes y un ticket de {formatCurrency(calculations.ticket)}, tus ingresos proyectados son <strong>{formatCurrency(calculations.ingresosProyectados)}</strong>.
                  </p>
                );
              })()}
            </div>

            {/* Párrafo 3 - Contexto del ROAS */}
            <div>
              {(() => {
                const roas = calculations.roasEsperado;
                if (roas >= 5) {
                  return (
                    <p className="text-slate-700 leading-relaxed">
                      Un ROAS de {roas.toFixed(1)}x indica que el escenario es muy favorable — probablemente el CPA objetivo está por debajo de lo que el mercado suele requerir. Es alcanzable si tu tasa de cierre y conversión son sólidas, pero conviene validarlo con datos reales.
                    </p>
                  );
                } else if (roas >= 2) {
                  return (
                    <p className="text-slate-700 leading-relaxed">
                      Un ROAS de {roas.toFixed(1)}x es un escenario equilibrado y alcanzable para la mayoría de los negocios de servicios con campañas bien optimizadas.
                    </p>
                  );
                } else {
                  return (
                    <p className="text-slate-700 leading-relaxed">
                      Un ROAS de {roas.toFixed(1)}x es bajo — por cada peso invertido recuperas poco más de lo que gastas. Considera si tu ticket o tu tasa de cierre pueden mejorar para aumentar el retorno.
                    </p>
                  );
                }
              })()}
            </div>

            {/* Párrafo 4 - Intensidad del Marketing */}
            <div>
              {(() => {
                const intCat = getIntensityCategory(calculations.intensidadMkt);
                const inversionPorCada100 = calculations.intensidadMkt;
                return (
                  <p className="text-slate-700 leading-relaxed">
                    Tu Intensidad del Marketing es <strong>{calculations.intensidadMkt.toFixed(1)}%</strong> <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${intCat.color}`}>{intCat.label}</span> — de cada $100 que generarás en ingresos, inviertes ${inversionPorCada100.toFixed(0)} en publicidad. Cuanto menor sea este porcentaje, más eficiente es tu marketing: logras el mismo resultado invirtiendo menos. <span className="text-slate-500">Referencia orientativa: menos del 10% es óptimo, entre 10% y 20% es moderado, más del 20% sugiere revisar tus tasas de conversión o ajustar el presupuesto.</span>
                  </p>
                );
              })()}
            </div>
          </motion.div>
        )}

        {/* Actions */}
        {calculations && (
          <div className="flex flex-wrap gap-4 mb-8">
            <Button 
              onClick={handleCopySummary}
              variant="outline" 
              className="gap-2 h-12"
              data-testid="copy-summary-button"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copiado' : 'Copiar Resumen'}
            </Button>

            <Button
              onClick={async () => {
                setExportingPdf(true);
                try {
                  const name = await exportPresupuestoPDF({ platform, calculations, viabilidad });
                  toast.success(`PDF generado: ${name}`);
                } catch (err) {
                  toast.error('No se pudo generar el PDF.');
                  // eslint-disable-next-line no-console
                  console.error(err);
                } finally {
                  setExportingPdf(false);
                }
              }}
              disabled={exportingPdf}
              className="gap-2 h-12 bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] text-white font-semibold"
              data-testid="export-pdf-button"
            >
              <FileDown className="h-4 w-4" />
              {exportingPdf ? 'Generando PDF…' : 'Descargar PDF'}
            </Button>
          </div>
        )}

        {/* DIY Simulator - only show when calculations are available */}
        {calculations && (
          <DIYSimulator 
            presupuestoMensual={calculations.presupuestoMensual}
            ingresosProyectados={calculations.ingresosProyectados}
            leadsNecesarios={calculations.leadsNecesarios}
            ticket={calculations.ticket}
          />
        )}

        {/* AI Analysis */}
        {calculations && (
          <AIAnalysis 
            module="presupuesto" 
            contextData={aiContext}
            disabled={!calculations}
          />
        )}
        </LeadGate>

        {/* Disclaimer */}
        <p className="text-xs text-slate-500 text-center mt-8">
          Los resultados son estimaciones basadas en las variables ingresadas. Los resultados reales dependen de la calidad creativa, la segmentación y el período de optimización del algoritmo.
        </p>
      </div>
    </div>
  );
}
